import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef
} from '@angular/core';
import { Subject, Observable, of, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, catchError, map } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { observeNoLoading } from '@web/util/loading/loading';
import { toast } from '@web/util/toast';
import { tr } from '@util/tr';
import { formatUtcTimestampRome } from '@util/utc-timestamp';
import { locale } from '@util/locale';
import { Host } from '@interfaces/host';
import { WsChipType } from '@web/util/ws-ui/ws-chip/ws-chip.component';
import { WsSearchbarConfig, WsSearchResult } from '@web/util/ws-ui/ws-searchbar/ws-searchbar.component';
import {
  countPaymentFilters,
  PAYMENTS_DEFAULT_PAGE_SIZE,
  PaymentFilter,
  PaymentsListFilterDialogComponent,
} from '../payments-list-filter-dialog/payments-list-filter-dialog.component';
import {
  PaymentsRefundDialogComponent,
  PaymentsRefundDialogResult,
} from '../payments-refund-dialog/payments-refund-dialog.component';
import {
  DwspayPaymentIntentDetails,
  DwspayPaymentSummary,
  WinesuitePaymentsListQuery,
  WSPaymentsService,
} from 'services/dws/ws-payments.service';
import { ContactsService } from 'services/dws/contacts.service';
import {
  normalizePaymentBrand,
  paymentBrandFromIntent,
  paymentBrandIcon as brandIconSrc,
  paymentBrandLabelKey,
} from '../payments-navigation.util';
import { PaymentsListExportService } from '../payments-list-export.service';
import { PaymentsListExportFormat } from '../payments-list-export';

// ── Types ──────────────────────────────────────────────────────────────────

export interface WsPaymentItem {
  id: string;
  chargeId?: string;
  /** Connected-account Stripe payment id (py_) for Connect embedded refund UI */
  connectPaymentId?: string;
  status: string;
  /** Mapped bucket, or the raw unmatched status (for debug). */
  displayStatus: 'succeeded' | 'failed' | 'refunded' | 'pending' | string;
  partialRefund: boolean;
  amountCents: number;
  amountRefundedCents: number;
  currency: string;
  created: number; // unix seconds
  // WS metadata
  customerName?: string;
  customerEmail?: string;
  saleId?: string;
  entityId?: string;
  /** Numeric WS order id when metadata carries order_id / wine_suite_order_id (T-5). */
  orderNumber?: string;
  /** Ecommerce shop display name from Stripe metadata when present (T-5). */
  shopName?: string;
  entityType?: string;
  portal?: string;
  origin?: string;
  engine?: string;
  moto?: string;
  // Payment method
  cardPresent: boolean;
  /** Stripe type actually used: card, card_present, klarna, satispay, … */
  paymentMethodType?: string;
  cardLast4?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  cardBrand?: string;
  cardCountry?: string;
  cvcCheck?: string;
  billingEmail?: string;
  billingAddress?: string;
  // Commissions (cents, lordo IVA)
  applicationFeeCents?: number;
  commissioneWspaymentsCents?: number;
  commissioneDivineaComCents?: number;
  // FX / settlement (from Stripe BalanceTransaction when presentment ≠ settlement)
  settlementAmountCents?: number;
  settlementCurrency?: string;
  exchangeRate?: number;
  // Refunds
  refunds: Array<{ id: string; amountCents: number; created: number; reason?: string; status: string }>;
  // Error
  lastErrorCode?: string;
  lastErrorMessage?: string;
}

const PAGE_SIZE_KEY = 'ws_payments_list_page_size';

const SUCCEEDED_STATUSES = new Set(['succeeded', 'captured', 'confirmed', 'partially_refunded']);
const FAILED_STATUSES = new Set(['failed', 'payment_failed', 'cancelled', 'canceled', 'error']);
const PENDING_STATUSES = new Set([
  'charged',
  'requires_capture',
  'requires_action',
  'requires_payment_method',
  'requires_confirmation',
  'opened',
  'processing',
  'pending',
]);

/** Same origin buckets as the advanced filter and wspayments GET /winesuite/payments. */
function classifyPaymentOrigin(
  origin?: string,
  portal?: string,
  entityType?: string,
  engine?: string,
  moto?: string,
): 'payment-link' | 'moto' | 'pos' | 'self-order' | 'divinea' | 'widget' {
  const originRaw = (origin ?? '').toLowerCase();
  const portalRaw = (portal ?? '').toLowerCase();
  const entityRaw = (entityType ?? '').toLowerCase();
  const engineRaw = (engine ?? '').toLowerCase();
  const blob = [originRaw, portalRaw, entityRaw, engineRaw].join(' ');

  if (blob.includes('payment-link') || blob.includes('payment_link') || engineRaw.includes('manual-payment-link')) {
    return 'payment-link';
  }
  if (
    engineRaw.includes('manual-insert-moto')
    || (moto ?? '').toLowerCase() === 'true'
    || originRaw.includes('moto')
    || originRaw.includes('ccv')
    || engineRaw.includes('moto')
  ) {
    return 'moto';
  }
  if (
    portalRaw.includes('wspaymentterminal')
    || portalRaw === 'terminal'
    || originRaw.includes('wspayments_terminal')
    || originRaw.includes('pos')
    || (originRaw.includes('terminal') && !portalRaw.includes('winesuite'))
  ) {
    return 'pos';
  }
  if (
    blob.includes('self-order')
    || blob.includes('self_order')
    || blob.includes('selforder')
    || blob.includes('self order')
  ) {
    return 'self-order';
  }
  if (portalRaw.includes('divinea') || originRaw.includes('divinea')) {
    return 'divinea';
  }
  return 'widget';
}

@Component({
  selector: 'web-payments-list-tab',
  templateUrl: './payments-list-tab.component.html',
  styleUrls: ['./payments-list-tab.component.scss'],
})
export class PaymentsListTabComponent implements OnInit, OnDestroy, OnChanges {
  @Input() company!: Host.Company;
  /** When true, hides list UI and shows only the payment detail panel (e.g. MatDialog host). */
  @Input() detailOnlyMode = false;
  /** Opens the detail panel for this PaymentIntent id on init (used from reservation/order links). */
  @Input() autoOpenPaymentIntentId?: string;
  /** When set (e.g. from reservation-detail), refunds persist on CRM reservation. */
  @Input() reservationId?: string;
  /** Optional CRM WS order number seeded when opening from order detail (T-5). */
  @Input() seedOrderNumber?: string;
  /** Optional shop name seeded when opening from order detail (T-5). */
  @Input() seedShopName?: string;
  @Output() detailClosed = new EventEmitter<void>();
  @Output() paymentChanged = new EventEmitter<WsPaymentItem>();

  /** Stable reference — required for mat-menu item clicks (do not use a getter here). */
  readonly exportFormatActions = [
    { id: 'csv', name: tr('CSV'), icon: undefined as string | undefined },
    { id: 'xlsx', name: tr('Excel'), icon: undefined as string | undefined },
  ];

  get isExportDisabled(): boolean {
    return this.totalElements === 0;
  }

  // ── Table state ──────────────────────────────────────────────────────────
  loading = false;
  payments: WsPaymentItem[] = [];
  totalElements = 0;
  pageIndex = 0;
  pageSize = PAYMENTS_DEFAULT_PAGE_SIZE;
  createdSortDir: 'asc' | 'desc' = 'desc';
  private lastWineryId?: string;

  readonly displayedColumns = ['created', 'status', 'customer', 'saleId', 'mode', 'commWs', 'commDivinea', 'amount'];

  // ── Search ───────────────────────────────────────────────────────────────
  searchText = '';
  private readonly search$ = new Subject<string>();

  readonly searchConfig: WsSearchbarConfig = {
    placeholder: tr('payments.list.searchPlaceholder'),
    debounceTime: 300,
    minSearchLength: 2,
    showIcon: true,
    showSpinner: false,
    showNoResultsMessage: false,
    showClearButton: true,
    showClearButtonAfterSearch: true,
    collapseOnBlurWhenEmpty: true,
    collapseOnClear: true,
    variant: 'toolbar',
  };

  dummySearchFunction = (_searchText: string): Observable<any[]> => of([]);

  dummyResultMapper = (_item: any): WsSearchResult => ({
    id: '',
    displayData: {},
  });

  // ── Filters ──────────────────────────────────────────────────────────────
  appliedFilters: PaymentFilter = {};

  get activeFiltersCount(): number {
    return countPaymentFilters(this.appliedFilters);
  }

  // ── Detail modal ─────────────────────────────────────────────────────────
  detailOpen = false;
  selectedPayment: WsPaymentItem | null = null;
  feeExpanded = false;

  get refundResidualCents(): number {
    if (!this.selectedPayment) return 0;
    return this.selectedPayment.amountCents - this.selectedPayment.amountRefundedCents;
  }

  // ── Error detail modal ────────────────────────────────────────────────────
  errorDetailOpen = false;

  // ── WsChipType ref ───────────────────────────────────────────────────────
  readonly WsChipType = WsChipType;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private wsPaymentsService: WSPaymentsService,
    private contactsService: ContactsService,
    public listExportService: PaymentsListExportService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['autoOpenPaymentIntentId']?.currentValue && this.detailOnlyMode) {
      this.openDetailByPaymentIntentId(changes['autoOpenPaymentIntentId'].currentValue);
    }
    if (!changes['company'] || !this.company?.id) return;
    const wineryChanged = this.company.id !== this.lastWineryId;
    this.lastWineryId = this.company.id;
    if (wineryChanged && !changes['company'].firstChange) {
      this.pageIndex = 0;
      this.appliedFilters = {};
      this.searchText = '';
      this.createdSortDir = 'desc';
      this.closeDetail();
      this.loadPayments();
    }
  }

  ngOnInit(): void {
    this.listExportService.running$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
    if (this.company?.id) {
      this.lastWineryId = this.company.id;
    }
    this.pageSize = this.loadPageSizePref();
    if (this.detailOnlyMode && this.autoOpenPaymentIntentId) {
      this.openDetailByPaymentIntentId(this.autoOpenPaymentIntentId);
      return;
    }
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageIndex = 0;
      if (!this.searchText || this.searchText.length >= 2) {
        this.loadPayments();
      }
    });
    this.loadPayments();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Load ─────────────────────────────────────────────────────────────────

  loadPayments(filters: PaymentFilter = this.appliedFilters): void {
    if (!this.company?.id) return;
    this.loading = true;

    const listQuery = this.buildListQuery(filters);

    observeNoLoading(
      this.wsPaymentsService.getWinesuitePayments(
        this.company.id,
        this.pageIndex,
        this.pageSize,
        listQuery,
      )
    ).subscribe({
      next: (page) => {
        const rawItems: DwspayPaymentSummary[] = page?.payments ?? [];
        const parsed = rawItems
          .map(r => this.parsePaymentSummary(r))
          .filter((p): p is WsPaymentItem => p !== null);

        this.enrichCustomerNames(parsed).pipe(takeUntil(this.destroy$)).subscribe((items) => {
          this.payments = items;
          this.totalElements = page?.totalElements ?? items.length;
          this.syncSelectedPaymentWithList();
          this.loading = false;
          this.cdr.markForCheck();
        });
      },
      error: (err) => {
        console.error('[payments.list] Error loading payments:', err);
        this.payments = [];
        this.totalElements = 0;
        this.loading = false;
        toast(tr('payments.list.loadError'));
        this.cdr.markForCheck();
      }
    });
  }

  private buildListQuery(filters: PaymentFilter = this.appliedFilters): WinesuitePaymentsListQuery {
    const query: WinesuitePaymentsListQuery = {};

    if (this.searchText?.trim().length >= 2) {
      query.search = this.searchText.trim();
    }
    if (filters?.status) {
      query.status = filters.status;
    }
    if (filters?.customerName?.trim()) {
      query.customerName = filters.customerName.trim();
    }
    if (filters?.customerEmail?.trim()) {
      query.customerEmail = filters.customerEmail.trim();
    }
    if (filters?.saleId?.trim()) {
      query.saleId = filters.saleId.trim();
    }
    if (filters?.mode) {
      query.mode = filters.mode;
    }
    if (filters?.origin?.trim()) {
      query.origin = filters.origin.trim();
    }
    if (filters?.amountMin != null && !Number.isNaN(filters.amountMin)) {
      query.amountMin = filters.amountMin;
    }
    if (filters?.amountMax != null && !Number.isNaN(filters.amountMax)) {
      query.amountMax = filters.amountMax;
    }
    if (filters?.dateFrom) {
      const start = new Date(filters.dateFrom);
      start.setHours(0, 0, 0, 0);
      query.dateFrom = Math.floor(start.getTime() / 1000);
    }
    if (filters?.dateTo) {
      const end = new Date(filters.dateTo);
      end.setHours(23, 59, 59, 999);
      query.dateTo = Math.floor(end.getTime() / 1000);
    }
    query.sortDir = this.createdSortDir;

    return query;
  }

  toggleDateSort(event: Event): void {
    event.stopPropagation();
    this.createdSortDir = this.createdSortDir === 'desc' ? 'asc' : 'desc';
    this.pageIndex = 0;
    this.loadPayments();
  }

  // ── Parsing ───────────────────────────────────────────────────────────────

  /**
   * Maps a known payment / PaymentIntent status to a display bucket.
   * Unmapped values are returned as-is (no succeeded/failed default) so they stay debuggable.
   * `confirmed` is the CRM/widget reservation state for a collected payment (old non-instant bookings).
   * A captured charge / amount_received with nothing left to capture wins over a stale requires_capture.
   */
  private resolveDisplayStatus(
    stripeStatus: string,
    amountCents: number,
    amountRefundedCents: number,
    extras?: { captured?: boolean; amountReceived?: number; amountCapturable?: number },
  ): string | null {
    const status = (stripeStatus ?? '').toLowerCase().trim();
    if (!status) {
      return null;
    }
    if (status === 'refunded') {
      return 'refunded';
    }
    if (FAILED_STATUSES.has(status)) {
      return 'failed';
    }

    const captured = extras?.captured === true;
    const amountReceived = extras?.amountReceived ?? 0;
    const amountCapturable = extras?.amountCapturable ?? 0;
    const collected = captured || (amountReceived > 0 && amountCapturable <= 0);

    if (SUCCEEDED_STATUSES.has(status) || collected) {
      if (amountCents > 0 && amountRefundedCents >= amountCents) {
        return 'refunded';
      }
      return 'succeeded';
    }
    if (PENDING_STATUSES.has(status)) {
      return 'pending';
    }
    return status;
  }

  private parsePaymentSummary(raw: DwspayPaymentSummary): WsPaymentItem | null {
    const metadata = raw.metadata || {};
    const amountCents = raw.amount ?? 0;
    const amountRefundedCents = raw.amountRefunded ?? raw.amount_refunded ?? 0;
    const stripeStatus = raw.status ?? '';
    const cardType = raw.paymentMethodType ?? '';

    const displayStatus = this.resolveDisplayStatus(
      stripeStatus,
      amountCents,
      amountRefundedCents,
    );
    if (!displayStatus) {
      return null;
    }

    const partialRefund = displayStatus === 'succeeded' && amountRefundedCents > 0 && amountRefundedCents < amountCents;

    return {
      id: raw.id,
      connectPaymentId: raw.connectPaymentId,
      status: stripeStatus,
      displayStatus,
      partialRefund,
      amountCents,
      amountRefundedCents,
      currency: (raw.currency ?? 'eur').toLowerCase(),
      created: raw.created ? Math.floor(raw.created / 1000) : Math.floor(Date.now() / 1000),
      customerName: this.displayCustomerName(
        metadata.customer_name ?? metadata.customerName,
        metadata.customer_email ?? metadata.customerEmail ?? raw.customer,
      ),
      customerEmail: metadata.customer_email ?? metadata.customerEmail ?? raw.customer,
      saleId: this.normalizeId(metadata.sale_id ?? metadata.saleId),
      entityId: this.normalizeId(metadata.entity_id ?? metadata.entityId),
      orderNumber: this.resolveOrderNumber(metadata),
      shopName: this.resolveShopName(metadata),
      entityType: metadata.entity_type ?? metadata.entityType,
      portal: metadata.portal,
      origin: metadata.origin,
      engine: metadata.engine,
      moto: metadata.moto,
      cardPresent: cardType === 'card_present',
      paymentMethodType: raw.paymentMethodType,
      cardLast4: raw.cardLast4,
      cardExpMonth: raw.cardExpMonth,
      cardExpYear: raw.cardExpYear,
      cardBrand: raw.cardBrand,
      refunds: [],
      applicationFeeCents: raw.applicationFeeTotal ?? 0,
      commissioneWspaymentsCents: raw.commissioneWspayments ?? 0,
      commissioneDivineaComCents: raw.commissioneDivineaCom ?? 0,
    };
  }

  private parsePaymentIntentDetail(details: DwspayPaymentIntentDetails): WsPaymentItem {
    const parsed = this.parsePaymentItem({
      paymentIntent: details.paymentIntent,
      intent: details.paymentIntent,
      connectPaymentId: details.connectPaymentId,
      applicationFeeTotal: details.applicationFeeTotal,
      commissioneWspayments: details.commissioneWspayments,
      commissioneDivineaCom: details.commissioneDivineaCom,
      status: details.status,
      amountRefunded: details.amountRefunded,
      refunds: details.refunds,
      created: details.created,
    });
    const refunds = details.refunds?.length
      ? details.refunds.map((r) => ({
          id: r.id ?? '',
          amountCents: Number(r.amount) || 0,
          created: Number(r.created) || 0,
          reason: this.resolveRefundReason(r),
          status: r.status ?? '',
        }))
      : parsed.refunds;
    const amountRefundedCents = Math.max(
      Number(details.amountRefunded) || 0,
      parsed.amountRefundedCents,
      refunds.reduce((sum, r) => sum + (r.amountCents || 0), 0),
    );
    const displayStatus = this.resolveDisplayStatus(
      parsed.status,
      parsed.amountCents,
      amountRefundedCents,
    ) ?? parsed.displayStatus;
    const partialRefund = displayStatus === 'succeeded'
      && amountRefundedCents > 0
      && amountRefundedCents < parsed.amountCents;
    return {
      ...parsed,
      displayStatus,
      partialRefund,
      amountRefundedCents,
      refunds,
      settlementAmountCents:
        details.settlementAmount != null ? Number(details.settlementAmount) : parsed.settlementAmountCents,
      settlementCurrency: details.settlementCurrency
        ? String(details.settlementCurrency).toLowerCase()
        : parsed.settlementCurrency,
      exchangeRate:
        details.exchangeRate != null && Number.isFinite(Number(details.exchangeRate))
          ? Number(details.exchangeRate)
          : parsed.exchangeRate,
    };
  }

  private parsePaymentItem(raw: any): WsPaymentItem {
    const intent = raw.paymentIntent || raw.intent || raw || {};
    const charge = this.resolveCharge(intent);
    const metadata = this.mergePaymentMetadata(intent, charge);
    const pmDetails = charge.payment_method_details || charge.paymentMethodDetails || {};
    const refunds = this.resolveRefunds(charge, raw.refunds);
    const amountRefundedCents = this.resolveAmountRefundedCents(charge, raw, refunds);

    const amountCents = intent.amount ?? raw.grossTotalCents ?? 0;
    const currency = (intent.currency ?? raw.currency ?? 'eur').toLowerCase();
    const stripeStatus = intent.status ?? raw.status ?? '';
    const cardType = pmDetails.type ?? ''; // 'card' | 'card_present'

    const displayStatus = this.resolveDisplayStatus(
      stripeStatus,
      amountCents,
      amountRefundedCents,
      {
        captured: charge?.captured === true,
        amountReceived: Number(intent.amount_received ?? intent.amountReceived) || 0,
        amountCapturable: Number(intent.amount_capturable ?? intent.amountCapturable) || 0,
      },
    ) ?? stripeStatus;

    const partialRefund = displayStatus === 'succeeded' && amountRefundedCents > 0 && amountRefundedCents < amountCents;

    const cardData = pmDetails.card || pmDetails.card_present || pmDetails.cardPresent || {};
    const paymentMethodType = this.resolvePaymentMethodType(pmDetails, intent);

    return {
      id: raw.externalId || intent.id || raw.id,
      chargeId: charge.id,
      connectPaymentId: raw.connectPaymentId,
      status: stripeStatus,
      displayStatus,
      partialRefund,
      amountCents,
      amountRefundedCents,
      currency,
      created: this.resolvePaymentCreatedSeconds(raw, intent),
      // WS metadata
      customerName: this.displayCustomerName(
        metadata.customer_name ?? metadata.customerName,
        metadata.customer_email ?? metadata.customerEmail,
      ),
      customerEmail: metadata.customer_email ?? metadata.customerEmail,
      saleId: this.normalizeId(metadata.sale_id ?? metadata.saleId ?? raw.saleId),
      entityId: this.normalizeId(metadata.entity_id ?? metadata.entityId ?? raw.entityId),
      orderNumber: this.resolveOrderNumber(metadata),
      shopName: this.resolveShopName(metadata),
      entityType: metadata.entity_type ?? metadata.entityType,
      portal: metadata.portal,
      origin: metadata.origin,
      engine: metadata.engine,
      moto: metadata.moto,
      // Payment method
      cardPresent: cardType === 'card_present',
      paymentMethodType,
      cardLast4: cardData.last4,
      cardExpMonth: cardData.exp_month,
      cardExpYear: cardData.exp_year,
      cardBrand: cardData.brand,
      cardCountry: cardData.country,
      cvcCheck: pmDetails.card?.checks?.cvc_check,
      billingEmail: charge.billing_details?.email,
      billingAddress: this.formatAddress(charge.billing_details?.address),
      // Fees (from backend split; fallback to Stripe application_fee on PI/charge)
      applicationFeeCents: raw.applicationFeeTotal ?? intent.application_fee_amount ?? charge.application_fee_amount ?? 0,
      commissioneWspaymentsCents: raw.commissioneWspayments ?? 0,
      commissioneDivineaComCents: raw.commissioneDivineaCom ?? 0,
      // Refunds
      refunds,
      // Error
      lastErrorCode: intent.last_payment_error?.code,
      lastErrorMessage: intent.last_payment_error?.message,
    };
  }

  /** Payment DB / API time first, then Stripe PI {@code created} (unix seconds). */
  private resolvePaymentCreatedSeconds(raw: any, intent: any): number {
    const fromApi = raw?.created;
    if (fromApi != null && Number(fromApi) > 0) {
      const n = Number(fromApi);
      return n > 10_000_000_000 ? Math.floor(n / 1000) : Math.floor(n);
    }
    if (raw?.createdAt) {
      const t = new Date(raw.createdAt).getTime();
      if (!Number.isNaN(t)) {
        return Math.floor(t / 1000);
      }
    }
    if (intent?.created != null && Number(intent.created) > 0) {
      return Math.floor(Number(intent.created));
    }
    return Math.floor(Date.now() / 1000);
  }

  private displayCustomerName(name?: string, email?: string): string | undefined {
    const n = (name ?? '').trim();
    if (!n) {
      return undefined;
    }
    const e = (email ?? '').trim().toLowerCase();
    if (n.includes('@') || (e && n.toLowerCase() === e)) {
      return undefined;
    }
    return n;
  }

  private enrichCustomerNames(items: WsPaymentItem[]): Observable<WsPaymentItem[]> {
    const missing = items.filter(p => !p.customerName && !!p.customerEmail?.includes('@'));
    if (missing.length === 0) {
      return of(items);
    }
    const uniqueEmails = [...new Set(missing.map(p => p.customerEmail!.trim().toLowerCase()))];
    const lookups = uniqueEmails.map(email =>
      this.contactsService.getByEmail(email).pipe(
        map(contact => {
          const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim();
          return [email, this.displayCustomerName(name, email)] as const;
        }),
        catchError(() => of([email, undefined] as const)),
      )
    );
    return forkJoin(lookups).pipe(
      map(pairs => {
        const byEmail = new Map(pairs.filter(([, name]) => !!name));
        return items.map(p => {
          if (p.customerName || !p.customerEmail) {
            return p;
          }
          const name = byEmail.get(p.customerEmail.trim().toLowerCase());
          return name ? { ...p, customerName: name } : p;
        });
      }),
      catchError(() => of(items)),
    );
  }

  private formatAddress(addr: any): string {
    if (!addr) return '';
    return [addr.line1, addr.line2, addr.city, addr.postal_code, addr.country]
      .filter(Boolean).join(', ');
  }

  // ── Search ────────────────────────────────────────────────────────────────

  onSearch(searchText: string): void {
    this.searchText = searchText;
    this.pageIndex = 0;
    this.loadPayments();
  }

  onSearchTextChange(searchText: string): void {
    this.searchText = searchText;
    if (!searchText) {
      this.pageIndex = 0;
      this.loadPayments();
      return;
    }
    this.search$.next(searchText);
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  openFilters(): void {
    const dialog = this.dialog.open(PaymentsListFilterDialogComponent, {
      width: 'min(850px, calc(100vw - 2rem))',
      maxWidth: 'calc(100vw - 1rem)',
      maxHeight: '90dvh',
      disableClose: true,
      panelClass: 'payments-list-filter-dialog',
      data: { appliedFilters: this.appliedFilters },
    });

    dialog.afterClosed().subscribe((filters: PaymentFilter | undefined) => {
      if (filters === undefined) return;
      this.appliedFilters = { ...filters };
      this.searchText = '';
      this.pageIndex = 0;
      this.loadPayments(this.appliedFilters);
    });
  }

  // ── Pagination ────────────────────────────────────────────────────────────

  onPageChange(evt: PageEvent): void {
    this.pageIndex = evt.pageIndex;
    this.pageSize = evt.pageSize;
    this.savePageSizePref(evt.pageSize);
    this.loadPayments();
  }

  private loadPageSizePref(): number {
    try {
      return parseInt(localStorage.getItem(PAGE_SIZE_KEY) ?? String(PAYMENTS_DEFAULT_PAGE_SIZE), 10)
        || PAYMENTS_DEFAULT_PAGE_SIZE;
    } catch {
      return PAYMENTS_DEFAULT_PAGE_SIZE;
    }
  }

  private savePageSizePref(size: number): void {
    try { localStorage.setItem(PAGE_SIZE_KEY, String(size)); } catch { }
  }

  // ── Detail modal ──────────────────────────────────────────────────────────

  openDetail(payment: WsPaymentItem): void {
    this.selectedPayment = payment;
    this.feeExpanded = false;
    this.detailOpen = true;
    this.errorDetailOpen = false;

    if (!this.company?.id) {
      return;
    }

    observeNoLoading(
      this.wsPaymentsService.getWinesuitePaymentIntent(this.company.id, payment.id)
    ).subscribe({
      next: (details) => {
        if (details?.paymentIntent) {
          this.selectedPayment = this.mergePaymentDetail(
            this.selectedPayment,
            this.parsePaymentIntentDetail(details),
          );
          this.patchPaymentInList(this.selectedPayment);
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading payment detail:', err);
        toast(tr('payments.list.loadError'));
        this.cdr.markForCheck();
      }
    });
  }

  /** Open detail overlay/dialog for a PaymentIntent id without a list row (calendar, order link). */
  openDetailByPaymentIntentId(paymentIntentId: string): void {
    const id = paymentIntentId?.trim();
    if (!id) {
      return;
    }
    const seedOrder = (this.seedOrderNumber || '').trim();
    const seedShop = (this.seedShopName || '').trim();
    this.openDetail({
      id,
      status: 'succeeded',
      displayStatus: 'succeeded',
      partialRefund: false,
      amountCents: 0,
      amountRefundedCents: 0,
      currency: 'eur',
      // 0 = unknown; merge prefers Stripe/detail time over this placeholder (T-5).
      created: 0,
      orderNumber: /^\d+$/.test(seedOrder) ? seedOrder : undefined,
      shopName: seedShop || undefined,
      cardPresent: false,
      refunds: [],
    });
  }

  closeDetail(): void {
    this.detailOpen = false;
    this.selectedPayment = null;
    if (this.detailOnlyMode) {
      this.detailClosed.emit();
    }
  }

  toggleFee(): void { this.feeExpanded = !this.feeExpanded; }

  // ── Refund (native WineSuite dialog + wspayments API) ────────────────────

  openRefundDialog(): void {
    if (!this.selectedPayment || !this.company?.id) {
      toast(tr('payments.list.refundError'));
      return;
    }

    const payment = this.selectedPayment;
    const residualCents = this.refundResidualCents;
    if (residualCents <= 0) {
      toast(tr('payments.list.refundInvalidAmount'));
      return;
    }

    const dialogRef = this.dialog.open(PaymentsRefundDialogComponent, {
      width: 'min(520px, calc(100vw - 2rem))',
      maxWidth: 'calc(100vw - 1rem)',
      disableClose: true,
      panelClass: 'payments-refund-dialog',
      data: {
        wineryId: this.company.id,
        paymentIntentId: payment.id,
        amountCents: residualCents,
        maxAmountCents: residualCents,
        currency: payment.currency,
        customerName: payment.customerName || payment.customerEmail,
        reservationId: this.reservationId,
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: PaymentsRefundDialogResult | undefined) => {
      if (!result?.confirmed) return;
      const refundedNowCents = result.amountCents ?? residualCents;
      this.applyLocalRefund(payment.id, refundedNowCents, result.reasonCode, result.notes);
      toast(tr('payments.list.refundSuccess'));
      this.refreshPaymentAfterRefund(payment.id);
    });
  }

  private refreshPaymentAfterRefund(paymentId: string): void {
    this.loadPayments();

    if (!this.detailOpen || !this.company?.id) return;

    observeNoLoading(
      this.wsPaymentsService.getWinesuitePaymentIntent(this.company.id, paymentId)
    ).subscribe({
      next: (details) => {
        if (!details?.paymentIntent || !this.detailOpen) return;
        this.selectedPayment = this.mergePaymentDetail(
          this.selectedPayment,
          this.parsePaymentIntentDetail(details),
        );
        this.patchPaymentInList(this.selectedPayment);
        this.paymentChanged.emit(this.selectedPayment);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error refreshing payment after refund:', err);
      },
    });
  }

  private applyLocalRefund(paymentId: string, refundedNowCents: number, reason?: string, notes?: string): void {
    const current = this.selectedPayment?.id === paymentId
      ? this.selectedPayment
      : this.payments.find((p) => p.id === paymentId);
    if (!current || refundedNowCents <= 0) {
      return;
    }
    const amountRefundedCents = current.amountRefundedCents + refundedNowCents;
    const displayStatus = this.resolveDisplayStatus(
      current.status,
      current.amountCents,
      amountRefundedCents,
    ) ?? current.displayStatus;
    const updated: WsPaymentItem = {
      ...current,
      amountRefundedCents,
      displayStatus,
      partialRefund: displayStatus === 'succeeded'
        && amountRefundedCents > 0
        && amountRefundedCents < current.amountCents,
      refunds: [
        ...current.refunds,
        {
          id: `refund-${Date.now()}`,
          amountCents: refundedNowCents,
          created: Math.floor(Date.now() / 1000),
          reason: notes ? `${reason || 'requested_by_customer'}: ${notes}` : reason,
          status: 'succeeded',
        },
      ],
    };
    this.selectedPayment = updated;
    this.patchPaymentInList(updated);
    this.cdr.markForCheck();
  }

  private patchPaymentInList(updated: WsPaymentItem | null): void {
    if (!updated?.id) {
      return;
    }
    const idx = this.payments.findIndex((p) => p.id === updated.id);
    if (idx < 0) {
      return;
    }
    this.payments = [
      ...this.payments.slice(0, idx),
      {
        ...this.payments[idx],
        amountRefundedCents: updated.amountRefundedCents,
        displayStatus: updated.displayStatus,
        partialRefund: updated.partialRefund,
        refunds: updated.refunds,
        status: updated.status,
      },
      ...this.payments.slice(idx + 1),
    ];
  }

  private syncSelectedPaymentWithList(): void {
    if (!this.selectedPayment) {
      return;
    }
    const fromList = this.payments.find((p) => p.id === this.selectedPayment!.id);
    if (!fromList) {
      return;
    }
    if (this.selectedPayment.amountRefundedCents > fromList.amountRefundedCents) {
      this.patchPaymentInList(this.selectedPayment);
      return;
    }
    if (fromList.amountRefundedCents > this.selectedPayment.amountRefundedCents) {
      this.selectedPayment = this.mergePaymentDetail(fromList, this.selectedPayment);
    }
  }

  // ── Error detail ──────────────────────────────────────────────────────────

  openErrorDetail(): void { this.errorDetailOpen = true; }
  closeErrorDetail(): void { this.errorDetailOpen = false; }

  getErrorLabel(code?: string): { desc: string; action: string } | null {
    if (!code) return null;
    const descKey = `payments.list.stripeError.${code}.desc`;
    const actionKey = `payments.list.stripeError.${code}.action`;
    const desc = tr(descKey);
    if (desc !== descKey) {
      const action = tr(actionKey);
      return { desc, action: action !== actionKey ? action : '' };
    }
    return { desc: code, action: '' };
  }

  private uiLocaleTag(): string {
    const iso = locale().locale;
    const map: Record<string, string> = {
      it: 'it-IT',
      en: 'en-GB',
      de: 'de-DE',
      fr: 'fr-FR',
      es: 'es-ES',
      pt: 'pt-PT',
      el: 'el-GR',
      hr: 'hr-HR',
    };
    return map[iso] ?? iso;
  }

  // ── Export ────────────────────────────────────────────────────────────────

  onExportFormatSelected(action: { id: string }): void {
    if (!this.company?.id || this.totalElements <= 0) {
      return;
    }
    if (action.id !== 'csv' && action.id !== 'xlsx') {
      return;
    }
    this.listExportService.startExport({
      wineryId: this.company.id,
      query: this.buildListQuery(),
      format: action.id as PaymentsListExportFormat,
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  formatDate(unix: number): string {
    if (!unix) {
      return '—';
    }
    return formatUtcTimestampRome(new Date(unix * 1000)) || '—';
  }

  formatCents(cents: number, currency = 'eur'): string {
    return (cents / 100).toLocaleString(this.uiLocaleTag(), {
      style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: 2
    });
  }

  statusLabel(p: WsPaymentItem): string {
    if (p.displayStatus === 'refunded') return tr('payments.list.statusRefunded');
    if (p.partialRefund) return tr('payments.list.statusPartiallyRefunded');
    if (p.displayStatus === 'failed') return tr('payments.list.statusFailed');
    if (p.displayStatus === 'pending') return tr('payments.list.statusPending');
    if (p.displayStatus === 'succeeded') return tr('payments.list.statusSucceeded');
    return p.displayStatus || p.status;
  }

  statusChipType(p: WsPaymentItem): WsChipType {
    if (p.displayStatus === 'refunded' || p.partialRefund) return WsChipType.INFO;
    if (p.displayStatus === 'failed') return WsChipType.ERROR;
    if (p.displayStatus === 'pending') return WsChipType.WARNING;
    if (p.displayStatus === 'succeeded') return WsChipType.SUCCESS;
    return WsChipType.DRAFT;
  }

  entityTypeLabel(type?: string): string {
    if (!type) return '';
    const t = type.toLowerCase();
    if (t.includes('payment-link') || t.includes('payment_link')) {
      return tr('payments.list.originPaymentLink');
    }
    if (t.includes('reservation')) return tr('payments.list.entityReservation');
    if (t.includes('self-order') || t.includes('self_order') || t.includes('selforder')) {
      return tr('payments.list.originSelfOrder');
    }
    if (t.includes('preorder')) return tr('payments.list.entityPreorder');
    if (t.includes('moto') || t.includes('ccv')) return tr('payments.list.originMoto');
    if (t.includes('order')) return tr('payments.list.entityOrder');
    if (t.includes('gift')) return tr('payments.list.entityGift');
    if (t.includes('mixed')) return tr('payments.list.entityMixed');
    return type;
  }

  transactionModeLabel(p: WsPaymentItem): string {
    const brandKey = paymentBrandLabelKey(p.paymentMethodType);
    if (brandKey) {
      return tr(brandKey);
    }
    return p.cardPresent ? 'Card Present' : 'Card Not Present';
  }

  paymentBrandIcon(p: WsPaymentItem | null | undefined): string {
    return brandIconSrc(p ? p.paymentMethodType : '');
  }

  originLabel(
    origin?: string,
    portal?: string,
    entityType?: string,
    engine?: string,
    moto?: string,
    shopName?: string,
  ): string {
    const key = classifyPaymentOrigin(origin, portal, entityType, engine, moto);
    if (key === 'widget') {
      const shop = (shopName || '').trim();
      if (shop) {
        return shop;
      }
      const host = this.originHostLabel(origin);
      if (host) {
        return host;
      }
    }
    const labels: Record<string, string> = {
      'payment-link': tr('payments.list.originPaymentLink'),
      moto: tr('payments.list.originMoto'),
      pos: tr('payments.list.originPos'),
      'self-order': tr('payments.list.originSelfOrder'),
      divinea: tr('payments.list.originDivinea'),
      widget: tr('payments.list.originWidget'),
    };
    return labels[key] ?? tr('payments.list.originWidget');
  }

  /** Prefer WS numeric order id over B2C UUID in the detail Ordine row (T-5). */
  entityDisplayId(p: WsPaymentItem | null | undefined): string {
    if (!p) {
      return '';
    }
    const entityType = (p.entityType || '').toLowerCase();
    const isOrderLike = entityType.includes('order');
    const candidates = [p.orderNumber, p.entityId, p.saleId];
    for (const c of candidates) {
      const v = (c ?? '').trim();
      if (/^\d+$/.test(v)) {
        return v;
      }
    }
    for (const c of candidates) {
      const v = this.normalizeId(c);
      if (!v) {
        continue;
      }
      if (isOrderLike && this.isUuid(v)) {
        continue;
      }
      return v;
    }
    return '';
  }

  wineryBadgeLabel(): string {
    return (this.company?.name || 'Wine Suite').toUpperCase();
  }

  /** Stripe-style detail header subtitle (customer / cardholder email). */
  paymentHeaderEmail(p: WsPaymentItem | null | undefined): string | null {
    if (!p) {
      return null;
    }
    const raw = (p.customerEmail ?? p.billingEmail ?? '').trim();
    if (!raw || !raw.includes('@')) {
      return null;
    }
    return raw;
  }

  formatFeeCents(cents: number, currency = 'eur'): string {
    const value = Number(cents) || 0;
    if (value === 0) {
      return this.formatCents(0, currency);
    }
    return `-${this.formatCents(Math.abs(value), currency)}`;
  }

  paymentMethodLabel(p: WsPaymentItem): string {
    const brandKey = paymentBrandLabelKey(p.paymentMethodType);
    if (brandKey) {
      return tr(brandKey);
    }
    return tr('payments.list.detailPaymentMethodCard');
  }

  cardTypeLabel(p: WsPaymentItem): string {
    if (!p.cardBrand) return '';
    const brand = p.cardBrand.charAt(0).toUpperCase() + p.cardBrand.slice(1);
    return `${brand} ${tr('payments.list.detailCardCredit')}`;
  }

  cvcCheckLabel(check?: string): string {
    if (!check) return '';
    if (check === 'pass') return tr('payments.list.cvcCheckPass');
    if (check === 'fail') return tr('payments.list.cvcCheckFail');
    if (check === 'unavailable') return tr('payments.list.cvcCheckUnavailable');
    return check;
  }

  countryLabel(code?: string): string {
    if (!code) return '';
    const iso = code.toUpperCase();
    try {
      const DisplayNames = (Intl as any).DisplayNames;
      if (typeof DisplayNames === 'function') {
        const lang = this.uiLocaleTag().split('-')[0];
        const name = new DisplayNames([lang], { type: 'region' }).of(iso);
        if (name) return `${code.toLowerCase()} ${name}`;
      }
    } catch { /* ignore */ }
    return code;
  }

  cvcCheckPassed(check?: string): boolean {
    return check === 'pass';
  }

  /** Charge metadata wins over PaymentIntent metadata (same source as billing-tool). */
  private mergePaymentMetadata(intent: any, charge: any): Record<string, string> {
    const merged: Record<string, string> = {};
    const intentMeta = intent?.metadata ?? {};
    const chargeMeta = charge?.metadata ?? {};
    for (const [k, v] of Object.entries(intentMeta)) {
      if (v != null) merged[k] = String(v);
    }
    for (const [k, v] of Object.entries(chargeMeta)) {
      if (v != null) merged[k] = String(v);
    }
    return merged;
  }

  isRealId(id?: string | null): boolean {
    return !!this.normalizeId(id);
  }

  adminIdDisplay(id?: string | null): string {
    const value = (id ?? '').trim();
    if (!value || !value.includes('-')) {
      return value;
    }
    return value.split('-')[0];
  }

  private normalizeId(id?: string | null): string | undefined {
    const value = (id ?? '').trim();
    const lower = value.toLowerCase();
    if (!value || lower === 'draft' || lower === 'manual-payment-link'
        || lower === 'manual-insert' || lower === 'manual-insert-moto') {
      return undefined;
    }
    return value;
  }

  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
  }

  private resolveOrderNumber(metadata: Record<string, string | undefined>): string | undefined {
    const candidates = [
      metadata.wine_suite_order_id,
      metadata.wineSuiteOrderId,
      metadata.order_id,
      metadata.orderId,
      metadata.ws_order_id,
      metadata.wsOrderId,
      metadata.entity_id,
      metadata.entityId,
    ];
    for (const raw of candidates) {
      const v = (raw ?? '').trim();
      if (/^\d+$/.test(v)) {
        return v;
      }
    }
    return undefined;
  }

  private resolveShopName(metadata: Record<string, string | undefined>): string | undefined {
    const candidates = [
      metadata.shop_name,
      metadata.shopName,
      metadata.ecommerce_shop_name,
      metadata.ecommerceShopName,
      metadata.installation_name,
      metadata.installationName,
      metadata.store_name,
      metadata.storeName,
    ];
    for (const raw of candidates) {
      const v = (raw ?? '').trim();
      if (v) {
        return v;
      }
    }
    return undefined;
  }

  private originHostLabel(origin?: string): string {
    const raw = (origin ?? '').trim();
    if (!raw || raw.toLowerCase() === 'widget') {
      return '';
    }
    try {
      const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      const host = new URL(withProto).hostname.replace(/^www\./i, '');
      if (host && host.includes('.')) {
        return host;
      }
    } catch {
      /* not a URL */
    }
    if (raw.includes('.') && !raw.includes(' ')) {
      return raw.replace(/^www\./i, '');
    }
    return '';
  }

  /**
   * Prefer Stripe/detail payment time over list placeholder (0 / Date.now) used when
   * opening detail from order/reservation without a list row (T-5).
   */
  private mergeCreatedSeconds(fromList?: number, detailed?: number): number {
    const list = Number(fromList) || 0;
    const detail = Number(detailed) || 0;
    if (detail > 0) {
      return detail;
    }
    if (list > 0) {
      return list;
    }
    return Math.floor(Date.now() / 1000);
  }

  private mergePaymentDetail(fromList: WsPaymentItem | null, detailed: WsPaymentItem): WsPaymentItem {
    if (!fromList) {
      return detailed;
    }
    const amountRefundedCents = Math.max(detailed.amountRefundedCents ?? 0, fromList.amountRefundedCents ?? 0);
    const displayStatus = this.resolveDisplayStatus(
      detailed.status || fromList.status,
      detailed.amountCents || fromList.amountCents,
      amountRefundedCents,
    ) ?? detailed.displayStatus;
    return {
      ...detailed,
      created: this.mergeCreatedSeconds(fromList.created, detailed.created),
      saleId: this.normalizeId(detailed.saleId) ?? this.normalizeId(fromList.saleId),
      entityId: this.normalizeId(detailed.entityId) ?? this.normalizeId(fromList.entityId),
      orderNumber: detailed.orderNumber || fromList.orderNumber,
      shopName: detailed.shopName || fromList.shopName,
      entityType: detailed.entityType || fromList.entityType,
      origin: detailed.origin || fromList.origin,
      portal: detailed.portal || fromList.portal,
      customerName: detailed.customerName || fromList.customerName,
      customerEmail: detailed.customerEmail || fromList.customerEmail,
      paymentMethodType: detailed.paymentMethodType || fromList.paymentMethodType,
      amountRefundedCents,
      refunds: detailed.refunds?.length ? detailed.refunds : fromList.refunds,
      displayStatus,
      partialRefund: displayStatus === 'succeeded'
        && amountRefundedCents > 0
        && amountRefundedCents < (detailed.amountCents || fromList.amountCents),
      settlementAmountCents: detailed.settlementAmountCents ?? fromList.settlementAmountCents,
      settlementCurrency: detailed.settlementCurrency || fromList.settlementCurrency,
      exchangeRate: detailed.exchangeRate ?? fromList.exchangeRate,
    };
  }

  private resolvePaymentMethodType(pmDetails: any, intent: any): string {
    const branded = normalizePaymentBrand(pmDetails?.type);
    if (branded) {
      return branded;
    }
    const type = String(pmDetails?.type ?? '').trim();
    if (type) {
      return type;
    }
    return paymentBrandFromIntent(intent) ?? '';
  }

  private resolveCharge(intent: any): any {
    const latest = intent?.latest_charge ?? intent?.latestCharge;
    if (latest && typeof latest === 'object' && !Array.isArray(latest)) {
      return latest;
    }
    const charges = intent?.charges?.data;
    if (Array.isArray(charges) && charges.length > 0) {
      return charges[0];
    }
    return {};
  }

  private resolveRefunds(
    charge: any,
    rawRefunds?: Array<{ id?: string; amount?: number; created?: number; reason?: string; status?: string }>,
  ): Array<{ id: string; amountCents: number; created: number; reason?: string; status: string }> {
    const list = charge?.refunds?.data
      ?? (Array.isArray(charge?.refunds) ? charge.refunds : null)
      ?? rawRefunds
      ?? [];
    if (!Array.isArray(list)) {
      return [];
    }
    return list.map((r: any) => ({
      id: r.id ?? '',
      amountCents: Number(r.amount ?? r.amountCents) || 0,
      created: Number(r.created) || 0,
      reason: this.resolveRefundReason(r),
      status: r.status ?? '',
    }));
  }

  private resolveRefundReason(raw: any): string | undefined {
    const meta = raw?.metadata ?? {};
    const code = meta.reason_code || raw.reason_code;
    const notes = meta.notes || raw.notes;
    const text = meta.reason_text || raw.reason_text || raw.reason;
    const head = code || text;
    if (!head) {
      return notes || undefined;
    }
    if (notes && !String(head).includes(String(notes))) {
      return `${head}: ${notes}`;
    }
    return String(head);
  }

  refundReasonLabel(reason?: string): string {
    if (!reason) {
      return '';
    }
    const trimmed = reason.trim();
    const colon = trimmed.indexOf(':');
    const head = (colon >= 0 ? trimmed.slice(0, colon) : trimmed).trim();
    const notes = colon >= 0 ? trimmed.slice(colon + 1).trim() : '';
    const code = this.normalizeRefundReasonCode(head);
    const label = code ? this.translateRefundReasonCode(code) : head;
    return notes ? `${label}: ${notes}` : label;
  }

  private normalizeRefundReasonCode(raw: string): string | null {
    const key = raw.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!key) {
      return null;
    }
    if (
      key === 'requested by customer' ||
      key === 'richiesto dal cliente' ||
      key === 'vom kunden angefordert' ||
      key === 'demande par le client' ||
      key === 'demandé par le client' ||
      key === 'solicitado por el cliente' ||
      key === 'solicitado pelo cliente' ||
      key.includes('requested by customer') ||
      key.includes('richiesto dal cliente')
    ) {
      return 'requested_by_customer';
    }
    if (key === 'duplicate' || key === 'duplicato' || key === 'duplikat' || key === 'doublon' || key === 'duplicado') {
      return 'duplicate';
    }
    if (key === 'fraudulent' || key === 'fraudolento' || key === 'betrügerisch' || key === 'frauduleux' || key === 'fraudulento') {
      return 'fraudulent';
    }
    return null;
  }

  private translateRefundReasonCode(code: string): string {
    switch (code) {
      case 'duplicate':
        return tr('payments.list.refundReasonDuplicate');
      case 'fraudulent':
        return tr('payments.list.refundReasonFraudulent');
      case 'requested_by_customer':
      default:
        return tr('payments.list.refundReasonRequestedByCustomer');
    }
  }

  private resolveAmountRefundedCents(
    charge: any,
    raw: any,
    refunds: Array<{ amountCents: number }>,
  ): number {
    const fromCharge = Number(charge?.amount_refunded ?? charge?.amountRefunded ?? 0);
    const fromRaw = Number(raw?.amountRefunded ?? raw?.amount_refunded ?? 0);
    const fromRefunds = refunds.reduce((sum, r) => sum + (Number(r.amountCents) || 0), 0);
    return Math.max(fromCharge, fromRaw, fromRefunds, 0);
  }

  cardExpiry(p: WsPaymentItem): string {
    if (!p.cardExpMonth || !p.cardExpYear) return '';
    return `${String(p.cardExpMonth).padStart(2, '0')} / ${p.cardExpYear}`;
  }

  totalCommissionCents(p: WsPaymentItem): number {
    if (p.applicationFeeCents != null) return p.applicationFeeCents;
    return (p.commissioneWspaymentsCents ?? 0) + (p.commissioneDivineaComCents ?? 0);
  }

  netCents(p: WsPaymentItem): number {
    return p.amountCents - this.totalCommissionCents(p);
  }

  /** Currency for fee/net in detail: settlement when FX applies (old Stripe embed style). */
  detailCurrency(p: WsPaymentItem): string {
    if (this.hasExchangeRate(p) && p.settlementCurrency) {
      return p.settlementCurrency;
    }
    return p.currency || 'eur';
  }

  /** Presentment cents → settlement cents when FX applies. */
  toDetailCents(p: WsPaymentItem, presentmentCents: number): number {
    if (!this.hasExchangeRate(p) || p.exchangeRate == null) {
      return presentmentCents;
    }
    return Math.round(presentmentCents * p.exchangeRate);
  }

  detailCommissionCents(p: WsPaymentItem): number {
    return this.toDetailCents(p, this.totalCommissionCents(p));
  }

  detailFeeWsCents(p: WsPaymentItem): number {
    return this.toDetailCents(p, p.commissioneWspaymentsCents ?? 0);
  }

  detailFeeDivineaCents(p: WsPaymentItem): number {
    return this.toDetailCents(p, p.commissioneDivineaComCents ?? 0);
  }

  /** Net in detail currency: settlement − converted platform fee when FX. */
  detailNetCents(p: WsPaymentItem): number {
    if (this.hasExchangeRate(p) && p.settlementAmountCents != null) {
      return p.settlementAmountCents - this.detailCommissionCents(p);
    }
    return this.netCents(p);
  }

  /** True when Stripe applied FX (presentment currency ≠ settlement). */
  hasExchangeRate(p: WsPaymentItem | null): boolean {
    if (!p?.exchangeRate || !p.settlementCurrency) return false;
    const presentment = (p.currency || '').toLowerCase();
    const settlement = (p.settlementCurrency || '').toLowerCase();
    return !!presentment && !!settlement && presentment !== settlement;
  }

  /** Presentment amount, or "USD -> EUR" when FX applies (Stripe embed style). */
  amountDetailLabel(p: WsPaymentItem): string {
    const presentment = this.formatCents(p.amountCents, p.currency);
    if (!this.hasExchangeRate(p) || p.settlementAmountCents == null) {
      return presentment;
    }
    return `${presentment} -> ${this.formatCents(p.settlementAmountCents, p.settlementCurrency)}`;
  }

  /** Stripe-style: "1,00 USD -> 0,878062 €". */
  exchangeRateLabel(p: WsPaymentItem): string {
    const rate = p.exchangeRate;
    if (rate == null || !Number.isFinite(rate)) return '—';
    const presentment = (p.currency || 'eur').toLowerCase();
    const settlement = (p.settlementCurrency || '').toLowerCase();
    const unit = this.formatCents(100, presentment);
    const rateFormatted = this.formatExchangeRate(rate, settlement);
    return `${unit} -> ${rateFormatted}`;
  }

  private formatExchangeRate(rate: number, currency: string): string {
    const code = (currency || 'eur').toUpperCase();
    const symbol = this.currencySymbol(code);
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(rate);
    return symbol ? `${formatted} ${symbol}` : `${formatted} ${code}`;
  }

  private currencySymbol(code: string): string {
    switch ((code || '').toUpperCase()) {
      case 'EUR':
        return '€';
      case 'USD':
        return 'USD';
      case 'GBP':
        return '£';
      case 'CHF':
        return 'CHF';
      default:
        return (code || '').toUpperCase();
    }
  }

  trackById(_: number, p: WsPaymentItem): string { return p.id; }
}
